<template>
    <div class="container-fluid w-100">
        <Navbar :title="$t('menu.video_recordings')" />

        <div class="d-flex flex-column mt-3">
            <!-- Search criteria card -->
            <div class="card p-3">
                <div class="fw-medium mb-2 text-success">
                    <font-awesome-icon icon="search" />
                    {{ $t('video_recordings.search_title') }}
                </div>
                <form
                    class="p-2"
                    @submit.prevent="onSearch"
                >
                    <div class="row nopads">
                        <div class="col-md-6 mb-2">
                            <label class="input-title">{{ $t('video_recordings.start_date') }}</label>
                            <date-time-picker
                                v-model:date="startDate"
                                :enable-time-picker="false"
                            />
                        </div>
                        <div class="col-md-6 mb-2">
                            <label class="input-title">{{ $t('video_recordings.end_date') }}</label>
                            <date-time-picker
                                v-model:date="endDate"
                                :enable-time-picker="false"
                            />
                        </div>

                        <div class="col-md-6 mb-2">
                            <label class="input-title">{{ $t('video_recordings.contract') }}</label>
                            <open-contract-filter
                                :from="startDate"
                                :to="endDate"
                                :show-company="false"
                                :show-title="false"
                                @contract-selected="onContractSelected"
                            />
                        </div>

                        <!-- Contractor -->
                        <div class="col-md-6 mb-2">
                            <span class="input-title">{{ $t('trip_list.search_with_company') }}</span>

                            <multiselect
                                v-model="companyParam"
                                class="multiselect_routa"
                                :placeholder="companyPlaceholder"
                                :deselect-label="$t('common.remove_selection')"
                                :select-label="$t('common.select')"
                                :close-on-select="true"
                                :disabled="companyOptions.length === 0"
                                label="name"
                                track-by="name"
                                :searchable="true"
                                :multiple="false"
                                :internal-search="true"
                                :selected-label="$t('common.selected')"
                                :options="companyOptions"
                                :loading="companyOptionsLoading"
                            >
                                <template
                                    slot="singleLabel"
                                    slot-scope="props"
                                >
                                    <span class="option__desc">
                                        <span class="option__title">{{ props.option.name }}</span>
                                    </span>
                                </template>
                                <template
                                    slot="option"
                                    slot-scope="props"
                                >
                                    <div class="option__desc">
                                        <span class="option__title multiselect_detail_block">{{ props.option.name }}</span>
                                    </div>
                                </template>

                                <span slot="noResult">{{ $t('common.not_found') }}</span>

                                <span slot="noOptions">{{ $t('common.no_results') }}</span>
                            </multiselect>
                        </div>

                        <div class="col-md-6 mb-2">
                            <label class="input-title">{{ $t('observations.order') }}</label>
                            <Multiselect
                                v-model="selectedOrder"
                                :options="orders"
                                :close-on-select="true"
                                label="name"
                                track-by="id"
                                :searchable="true"
                                :internal-search="true"
                                :selected-label="$t('common.selected')"
                                :select-label="$t('common.select')"
                                :deselect-label="$t('common.remove_selection')"
                                :placeholder="$t('orders.select_status')"
                                :loading="ordersLoading"
                                :disabled="!selectedContract"
                            >
                                <template #option="{ option }">
                                    <div>{{ option.name }}</div>
                                </template>
                                <template #noResult>
                                    <span>{{ $t('common.not_found') }}</span>
                                </template>
                                <template #noOptions>
                                    <span>{{ $t('common.no_results') }}</span>
                                </template>
                            </Multiselect>
                        </div>
                    </div>
                    <div class="d-flex justify-content-end mt-2">
                        <button
                            type="submit"
                            class="btn btn-success"
                            :disabled="loading || !startDate"
                        >
                            <font-awesome-icon
                                icon="search"
                                class="me-1"
                            />
                            {{ $t('video_recordings.search') }}
                        </button>
                    </div>
                </form>
            </div>

            <!-- Results card -->
            <div class="card mt-3 p-4">
                <!-- Empty state before search -->
                <div
                    v-if="!hasSearched"
                    class="d-flex flex-column align-items-center justify-content-center text-center text-muted py-5 px-3"
                >
                    <font-awesome-icon
                        icon="video"
                        class="empty-state-icon text-success mb-3"
                    />
                    <div class="small">
                        {{ $t('video_recordings.empty_state') }}
                    </div>
                </div>

                <!-- Results after search -->
                <div v-else>
                    <!-- Results header -->
                    <div class="mb-3">
                        <h5 class="fw-bold mb-1">
                            {{ $t('video_recordings.results_title') }}
                            <span class="fw-normal text-muted small">({{ workTrips.length }} {{
                                $t('video_recordings.recordings_count') }})</span>
                        </h5>
                        <div class="text-muted small">
                            <font-awesome-icon
                                icon="layer-group"
                                class="me-1"
                            />
                            {{ $t('video_recordings.select_route_hint') }}
                        </div>
                    </div>

                    <!-- Two-column layout: list + map -->
                    <div class="row">
                        <div class="small fw-medium py-2">
                            <font-awesome-icon
                                icon="route"
                                class="me-1"
                            />
                            {{ $t('video_recordings.trip_list_title') }} ({{ workTrips.length }})
                        </div>
                        <div class="col-12 col-lg-6 mb-3 mb-md-0 pe-md-2">
                            <video-recordings-list
                                :trips="workTrips"
                                :selected-trip-id="selectedTripId"
                                @trip-selected="onTripSelected"
                                @trip-hover="onTripHover"
                                @trip-hover-exit="onTripHoverExit"
                            />
                        </div>
                        <div class="col-12 col-lg-6 ps-md-2">
                            <div class="map-wrapper">
                                <map-container
                                    ref="mapContainer"
                                    :find-user="false"
                                    :menu-enabled="false"
                                    :center="mapCenter"
                                    @on-map-changed="onOverviewMapReady"
                                    @on-map-clicked="onMapClicked"
                                    @on-polyline-hover="onMapPolylineHover"
                                    @on-polylines-hovered="onMapPolylinesHovered"
                                    @on-polyline-hover-exit="onMapPolylineHoverExit"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Bottom hint -->
                    <div class="text-center text-muted small border-top mt-3 pt-3">
                        {{ $t('video_recordings.select_route_detail_hint') }}
                    </div>
                </div>
            </div>

            <!-- Detail view when a trip is selected -->
            <video-recordings-detail
                v-if="selectedTrip"
                ref="videoPlayer"
                :trip="selectedTrip"
                :trip-detail="tripDetail"
                :video-segments="videoSegments"
                :video-segments-loading="videoSegmentsLoading"
                :observations="tripObservations"
                :show-all-observations="showAllObservations"
                @marker-selected="onMarkerSelected"
                @observation-created="onObservationCreated"
                @toggle-show-all-observations="toggleAllObservations"
            />

            <!-- Observations for selected trip -->
            <video-observations
                v-if="selectedTrip"
                :key="observationsKey"
                :observations="tripObservations"
                :observations-loading="tripObservationsLoading"
                :selected-observation-id="selectedObservationId"
                @seek-observation="onSeekObservation"
            />
        </div>

        <!-- Overlapping-route picker: shown when a map click lands on several
             routes stacked on the same road. Lets the user choose which trip to
             open; selecting one drives the normal selection path
             (onTripSelected), i.e. it sets selectedTripId. -->
        <base-modal
            v-if="selectTripModal"
            hide-footer
            :title="$t('video_recordings.select_trip_modal_title')"
            @close="selectTripModal = false"
        >
            <div class="container">
                <div
                    v-for="trip in overlappingTrips"
                    :key="trip.id"
                    class="row py-2 trip-select-row"
                    :style="{ borderLeftColor: trip.color || '#ccc' }"
                    @click.stop="onOverlappingTripSelected(trip)"
                >
                    <div class="col-12">
                        <div class="small">
                            <span class="fw-semibold me-2">{{ trip.license_plate }}</span>
                            <span class="text-muted">({{ trip.vehicle_owner }})</span>
                            <span class="text-muted"> - {{ trip.task }}</span>
                        </div>
                        <div class="small text-muted mt-1">
                            {{ toLocalTime(trip.start_time) }} - {{ toLocalTime(trip.end_time) }}
                        </div>
                    </div>
                </div>
            </div>
        </base-modal>

        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import Navbar from '../utils/Navbar.vue';
import DateTimePicker from '../common/DateTimePicker.vue';
import VideoRecordingsList from './VideoRecordingsList.vue';
import VideoObservations from './VideoObservations.vue';
import VideoRecordingsDetail from './VideoRecordingsDetail.vue';
import MapContainer from '../map/MapContainer.vue';
import OpenContractFilter from '../contract/OpenContractFilter.vue';
import BaseModal from '../BaseModal.vue';
import Multiselect from 'vue-multiselect'

import { restApi } from '../mixins/RestApiMixin';
import { mapHelper } from '../mixins/MapMixin';
import { timeUtils } from '../mixins/TimeUtils';

export default {
    name: "VideoRecordings",
    components: {
        Navbar,
        DateTimePicker,
        VideoRecordingsList,
        VideoRecordingsDetail,
        VideoObservations,
        MapContainer,
        Multiselect,
        OpenContractFilter,
        BaseModal
    },
    mixins: [restApi, mapHelper, timeUtils],
    props: {
        user: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            loading: false,
            hasSearched: false,
            selectedContract: null,
            selectedOrder: null,
            orders: [],
            ordersLoading: false,
            startDate: null,
            endDate: null,
            workTrips: [],
            selectedTripId: null,
            selectedTrip: null,
            tripDetail: null,
            tripObservations: [],
            tripObservationsLoading: false,
            // Observation id currently highlighted in the table (driven by a
            // map marker tap).
            selectedObservationId: null,
            videoSegments: [],
            videoSegmentsLoading: false,
            // Aborts the previous trip's in-flight requests when a new trip is
            // selected, so fast clicking through the list can't let a stale
            // response overwrite the current selection (which wedged the player).
            tripRequestController: null,
            map: null,
            mapCenter: { lat: 64.0, lng: 26.0 },
            // Route line widths (px). Highlighted route is drawn wider.
            DEFAULT_ROUTE_WIDTH: 4,
            HIGHLIGHT_ROUTE_WIDTH: 8,
            // Id of the trip route currently thickened on the map, if any.
            highlightedRouteId: null,
            // Overlapping-route picker: when a map click hits several stacked
            // routes, these hold the candidates and toggle the chooser modal.
            selectTripModal: false,
            overlappingTrips: [],
            // Routes currently under the cursor, as detected by the hover
            // handlers. A map click acts on this set (see onMapClicked).
            hoveredTrips: [],
            showAllObservations: false,
            companyOptions: [],
            companyParam: [],
            companyPlaceHolder: "",
            companyOptionsLoading: false
        }
    },
    computed: {
        // Remount the observations subtree whenever the observation set
        // changes (create / remove / trip switch). The underlying
        // @bhplugin/vue3-datatable does not reliably re-render when its rows
        // are reassigned after mount, so keying the whole subtree on the ids
        // forces a fresh render with the new data.
        observationsKey() {
            return this.tripObservations.map(o => o.id).join('-')
        }
    },
    watch: {
        selectedContract(newVal) {
            this.selectedOrder = null
            this.orders = []
            if (newVal) {
                this.fetchOrders(newVal.id)
            }
        },
        showAllObservations() {
            // refetch the observations
            this.fetchTripObservations(this.selectedTrip)
        }
    },
    beforeUnmount() {
        // Abort any requests still in flight so their callbacks don't run
        // against a destroyed component.
        if (this.tripRequestController) {
            this.tripRequestController.abort()
        }
    },
    methods: {
        onContractSelected(contract) {
            // Driven by OpenContractFilter's contractSelected event (fired both
            // on manual selection and on its built-in single-contract
            // auto-select). Setting selectedContract triggers the existing
            // watcher, which resets and reloads the order list.
            this.selectedContract = contract || null
        },

        fetchOrders(contractId) {
            this.ordersLoading = true
            this.restFetchParams(this.orderUrl, { contract_ids: [contractId] }, this.handleOrders, this.onOrdersError)
        },

        getContractCompanies: function () {
            this.companyOptions = []
            this.companyPlaceholder = this.$t('trip_list.search_select_contract_first')

            if (this.selectedContract) {
                this.restFetchParams(this.contractCompanyUrl, { contract: this.selectedContract.id }, this.handleContractCompanies)
            }
        },

        handleContractCompanies: function (response) {
            this.companyOptionsLoading = true

            if (response && response.data) {
                this.companyPlaceholder = this.$t('')
                response.data.forEach(company => {
                    this.companyOptions.push({ name: company.name, value: company.id })
                })
                this.companyPlaceholder = this.$t('users.select_company')
            }

            this.companyOptionsLoading = false
        },

        handleOrders(response) {
            this.ordersLoading = false
            if (response && response.data) {
                this.orders = response.data.map(order => {
                    const taskName = order.task_type
                        ? order.task_type.name
                        : (order.contract_task_type ? order.contract_task_type.task_type.name : '')
                    const info = order.info ? ' - ' + order.info : ''
                    return {
                        ...order,
                        name: order.id + ' - ' + taskName + info
                    }
                })

                this.getContractCompanies()
            }
        },

        onOrdersError() {
            this.ordersLoading = false
        },

        onSearch() {
            const params = {
                hasVideoRecords: true
            }

            if (this.selectedContract) {
                params.contracts = [this.selectedContract.id]
            }

            if (this.selectedOrder) {
                params.orders = [this.selectedOrder.id]
            }

            if (this.startDate) {
                params.from = this.getStartOfTheDay(this.startDate)
            }

            if (this.endDate) {
                params.to = this.getEndOfTheDay(this.endDate)
            }

            if (this.companyParam && this.companyParam.value) {
                params.companyId = this.companyParam.value
            }

            this.hasSearched = true
            this.selectedTripId = null
            this.selectedTrip = null
            this.workTrips = []

            this.loading = true
            this.restFetchParams(this.workTripUrl, params, this.handleWorkTrips, this.onError)
        },

        handleWorkTrips(response) {
            this.loading = false
            if (response && response.data) {
                this.workTrips = response.data
                this.$nextTick(() => {
                    this.drawTripsOnMap()
                })
            }
        },

        onOverviewMapReady(map) {
            this.map = map
            if (this.workTrips.length > 0) {
                // Trips already loaded before the map finished initializing
                // (common on the first search): draw and zoom to them now.
                this.drawTripsOnMap()
            } else {
                // No results yet: show the default country-level view.
                this.map.zoomToPosition(64.0, 26.0, 6)
            }
        },

        drawTripsOnMap() {
            // The overview map only mounts after the first search, so it may
            // not be ready when results arrive. If so, onOverviewMapReady will
            // call this again once the map exists.
            if (!this.map) return
            this.map.removePolylinesByType(this.TRACE)
            this.workTrips.forEach((trip) => {
                if (trip.geometry && trip.geometry.points && trip.geometry.points.length > 1) {
                    const color = trip.color || '#7FC000'
                    const points = trip.geometry.points.map(p => ({ x: p[0], y: p[1] }))
                    this.map.drawPolyLine(trip.id, this.TRACE, points, color, false, false, this.DEFAULT_ROUTE_WIDTH)
                }
            })
            // Re-apply the highlight to the selected route after a full redraw.
            this.highlightRoute(this.selectedTripId)
            this.$nextTick(() => {
                if (this.map) {
                    // Ensure Leaflet has the correct container size before
                    // fitting bounds — the map may have just been revealed.
                    this.map.resizeMap()
                    this.map.zoomToGroup(this.TRACE)
                }
            })
        },

        // Thickens the given trip's route and brings it to the front; resets the
        // previously highlighted route to the default width. Passing null clears
        // the highlight. Restyles existing polylines in place (no redraw), the
        // same approach used by the route optimization simulation preview.
        highlightRoute(tripId) {
            if (!this.map || typeof this.map.setPolylineStyle !== 'function') return

            // Reset the previously highlighted route (unless it is the one we are
            // about to highlight again).
            if (this.highlightedRouteId !== null && this.highlightedRouteId !== tripId) {
                this.map.setPolylineStyle(
                    this.highlightedRouteId,
                    this.TRACE,
                    { weight: this.DEFAULT_ROUTE_WIDTH }
                )
            }

            if (tripId !== null && tripId !== undefined) {
                this.map.setPolylineStyle(
                    tripId,
                    this.TRACE,
                    { weight: this.HIGHLIGHT_ROUTE_WIDTH },
                    true
                )
            }
            this.highlightedRouteId = (tripId === undefined) ? null : tripId
        },

        // List row hover: preview the route by highlighting it.
        onTripHover(trip) {
            if (trip) {
                this.highlightRoute(trip.id)
            }
        },

        // List row hover exit: fall back to the selected route's highlight
        // (or clear it when nothing is selected).
        onTripHoverExit() {
            this.highlightRoute(this.selectedTripId)
        },

        onMapPolylinesHovered(hits, point) {
            if (!this.map || !point) return
            // Record what's under the cursor so a click can act on the same set
            // the hover detected (the map's own click path uses different,
            // vertex-based matching that misses mid-segment clicks on sparse GPS
            // routes — hover uses pixel-to-segment distance and is reliable).
            const trips = hits
                .filter(hit => hit.lineData && hit.lineData.type === this.TRACE)
                .map(hit => this.workTrips.find(t => t.id === hit.lineData.id))
                .filter(Boolean)
            this.hoveredTrips = trips

            let label = '<div>'
            trips.forEach(trip => {
                label += this.buildTripLabelRow(trip)
            })
            label += '</div>'
            this.map.removeGeoLabels('tripHoverLabel')
            this.map.addGeoLabel(point.lat, point.lng, label, 'tripHoverLabel')
        },

        // Exactly one route under the cursor: label just that one.
        onMapPolylineHover(data, latlng) {
            if (!this.map || !latlng || !data || data.type !== this.TRACE) return
            const trip = this.workTrips.find(t => t.id === data.id)
            if (!trip) return
            this.hoveredTrips = [trip]
            const label = '<div>' + this.buildTripLabelRow(trip) + '</div>'
            this.map.removeGeoLabels('tripHoverLabel')
            this.map.addGeoLabel(latlng.lat, latlng.lng, label, 'tripHoverLabel')
        },

        // Cursor left the route(s): clear the tooltip and the under-cursor set.
        onMapPolylineHoverExit() {
            this.hoveredTrips = []
            if (this.map) {
                this.map.removeGeoLabels('tripHoverLabel')
            }
        },

        buildTripLabelRow(trip) {
            const color = trip.color || '#7FC000'
            const plate = trip.license_plate || ''
            const owner = trip.vehicle_owner ? ` (${trip.vehicle_owner})` : ''
            const task = trip.task ? ` - ${trip.task}` : ''
            const dotStyle = `display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 6px; background-color: ${color}`
            return (
                `<div class="nopads" style="display: flex; align-items: center;">` +
                    `<span style="${dotStyle}"></span>` +
                    `<span class="nopads"><strong>${plate}</strong>${owner}${task}</span>` +
                `</div>` +
                `<div class="nopads pl-2 mb-1" style="color: #949494">${this.toLocalTime(trip.start_time)} - ${this.toLocalTime(trip.end_time)}</div>`
            )
        },

        // --- Map click selection ---------------------------------------------
        // A click on the map acts on whatever routes the HOVER detection last
        // found under the cursor (`hoveredTrips`). We deliberately drive this
        // off the hover set rather than the map's own polyline-tap/multi-tap
        // events: those use vertex-distance matching in metres and miss clicks
        // on the long straight segments between sparse GPS points, so clicking
        // a line that clearly highlights on hover would do nothing. The hover
        // path uses pixel-to-segment distance and matches reliably.
        //   0 routes -> ignore (clicked empty map)
        //   1 route  -> select it
        //   2+ routes-> open the chooser modal
        onMapClicked() {
            const trips = this.hoveredTrips
            if (!trips || trips.length === 0) {
                return
            }
            if (trips.length === 1) {
                this.onTripSelected(trips[0])
                return
            }
            this.overlappingTrips = trips
            this.selectTripModal = true
        },

        // User picked a trip from the overlapping-route modal: select it and
        // close the chooser.
        onOverlappingTripSelected(trip) {
            this.selectTripModal = false
            this.overlappingTrips = []
            if (trip) {
                this.onTripSelected(trip)
            }
        },

        onTripSelected(trip) {
            if (trip.id === this.selectedTripId) {
                return
            }

            // Cancel any requests still in flight from the previously selected
            // trip so their responses can't clobber this selection.
            if (this.tripRequestController) {
                this.tripRequestController.abort()
            }
            this.tripRequestController = new AbortController()
            const signal = this.tripRequestController.signal

            this.selectedTripId = trip ? trip.id : null
            this.selectedTrip = trip
            this.tripDetail = null

            // Clear videosegments
            this.videoSegments = []
            this.videoSegmentsLoading = false

            // Clear observations from any previously selected trip
            this.tripObservations = []

            if (trip) {
                const params = {
                    vehicle: trip.vehicle_id,
                    contract: trip.contract_id,
                    order: trip.order_id,
                    from: trip.start_time,
                    to: trip.end_time,
                    with_geom: 1,
                    timestamps: 1
                }

                this.restFetchParams(this.tripUrl, params, this.handleTripDetail, this.onError, signal)

                // Fetch observations reported during this trip's time window
                // for the same contract.
                this.fetchTripObservations(trip, signal)

                if (trip.work_session_id) {
                    this.videoSegmentsLoading = true
                    this.restFetch(this.dashcamVideoRecordsUrl + '/' + trip.work_session_id, this.handleVideoRecordsResponse, this.onVideoRecordsError, signal)

                    // Scroll to the player as soon as it's rendered (the
                    // detail view is v-if'd on selectedTrip, so it isn't in
                    // the DOM yet at this point). Don't wait for the fetch.
                    this.$nextTick(() => {
                        const element = this.$refs.videoPlayer?.$el
                        element?.scrollIntoView({ behavior: 'smooth' })
                    })
                }
            }

            // Persist the highlight on the newly selected route.
            this.highlightRoute(this.selectedTripId)
        },

        handleVideoRecordsResponse(response) {
            this.videoSegmentsLoading = false
            if (response && response.data) {
                // For data integrity reasons we filter out the duplicate segments by item.segmentIndex
                const segments = response.data

                const filterDuplicateSegments = (array) => {
                    const segmentIndexes = new Set();

                    return array.filter(item => {
                        const segmentIndex = item.segmentIndex;
                        const isDuplicate = segmentIndexes.has(segmentIndex)

                        if (!isDuplicate) {
                            segmentIndexes.add(item.segmentIndex)
                        }

                        return !isDuplicate;
                    });

                };

                this.videoSegments = filterDuplicateSegments(segments)
            }
        },

        onVideoRecordsError() {
            this.videoSegmentsLoading = false
            this.videoSegments = []
        },

        handleTripDetail(response) {
            if (response && response.data && response.data.length > 0) {
                this.tripDetail = response.data[0]
            }
        },

        toggleAllObservations(value) {
            this.showAllObservations = value
        },

        fetchTripObservations(trip, signal = null) {
            if (!trip) return

            const observationParams = {
                contract: trip.contract_id,
                workTripId: trip.id
            }

            if (!this.showAllObservations) {
                observationParams.from = trip.start_time
                observationParams.to = trip.end_time
                observationParams.order = trip.order_id
                observationParams.empty_order = true
            }

            this.tripObservationsLoading = true
            this.restFetchParams(this.observationUrl, observationParams, this.handleTripObservations, this.onObservationsError, signal)
        },

        handleTripObservations(response) {
            this.tripObservationsLoading = false
            this.tripObservations = (response && response.data) ? response.data : []
        },

        // Table-row "jump to" click: forward to the detail view, which owns
        // the video player + map marker sync.
        onSeekObservation(observation) {
            const detail = this.$refs.videoPlayer
            if (detail && typeof detail.seekToObservation === 'function') {
                detail.seekToObservation(observation)
            }
        },

        // Map marker tap in the detail view: highlight the matching table row.
        onMarkerSelected(id) {
            this.selectedObservationId = id
        },

        // A new observation was created via the wizard in the detail view:
        // refetch this trip's observations so the table + map markers update.
        onObservationCreated() {
            this.fetchTripObservations(this.selectedTrip)
        },

        onObservationsError() {
            this.tripObservationsLoading = false
            // On the initial trip load there is nothing to preserve; on a
            // refetch (e.g. after creating an observation) keep the last known
            // observations so a transient failure doesn't wipe the table/map.
            if (!this.tripObservations || this.tripObservations.length === 0) {
                this.tripObservations = []
            }
        },

        onError() {
            this.loading = false
        }
    }
}
</script>

<style scoped>
.input-title {
    font-size: .9em;
    color: #404041;
}

.empty-state-icon {
    font-size: 3em;
}

.map-wrapper {
    position: relative;
    width: 100%;
    height: 45vh;
    border: 1px solid #dee2e6;
    border-radius: .375rem;
    overflow: hidden;
}

.trip-select-row {
    border-bottom: 1px solid #d9d9d9;
    border-left: 4px solid #ccc;
    cursor: pointer;
    transition: background 0.15s ease;
}

.trip-select-row:hover {
    background: #dff1bd;
}
</style>
